import { useMutation } from "@blitzjs/rpc"
import { AuthenticationError } from "blitz"
import { InputAdornment } from "@mui/material"
import { useTranslation } from "next-i18next"
import EmailOutline from "mdi-material-ui/EmailOutline"
import LockOutline from "mdi-material-ui/LockOutline"
import { useRouter } from "next/router"
import { useSearchParams } from "next/navigation"
import { Login } from "src/app/auth/validations"
import { Form, FORM_ERROR } from "src/app/core/components/Form"
import { FormTextField } from "src/app/core/components/formFields/FormTextField"
import { SubmitButton } from "src/app/core/components/formFields/SubmitButton"
import login from "src/app/auth/mutations/login"

type Props = { schoolSlug: string }

const LoginCodeForm = ({ schoolSlug }: Props) => {
  const { t } = useTranslation("common")
  const router = useRouter()
  const searchParams = useSearchParams()
  const loginCode = searchParams.get("code")
  const [loginMutation] = useMutation(login)

  return (
    <Form
      schema={Login}
      initialValues={{ email: "", password: loginCode ?? "" }}
      onSubmit={async (values) => {
        try {
          await loginMutation({ ...values, useCode: true })
          void router.push(`/${schoolSlug}`)
        } catch (error: any) {
          if (error instanceof AuthenticationError) {
            return { [FORM_ERROR]: "Sorry, those credentials are invalid" }
          }
          return {
            [FORM_ERROR]:
              "Sorry, we had an unexpected error. Please try again. - " + error.toString(),
          }
        }
      }}
    >
      <FormTextField
        name="email"
        label={t("fieldEmailForCode")}
        placeholder={t("fieldEmailForCodePlaceholder")}
        helperText={t("fieldEmailForCodeHelper")}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailOutline sx={{ opacity: 0.5 }} />
            </InputAdornment>
          ),
        }}
      />
      <FormTextField
        name="password"
        label={t("fieldLoginCode")}
        placeholder={t("fieldLoginCodePlaceholder")}
        helperText={t("fieldLoginCodeHelper")}
        transform={{
          output: (e) => ({
            ...e,
            target: { ...e.target, value: e.target.value.toUpperCase() },
          }),
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutline sx={{ opacity: 0.5 }} />
            </InputAdornment>
          ),
        }}
      />
      <SubmitButton sx={{ mt: 2 }} fullWidth size="large" variant="contained">
        {t("btnLogin")}
      </SubmitButton>
    </Form>
  )
}

export default LoginCodeForm
